import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "../api/client";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface HandSummary {
  id: string;
  name: string;
  description: string;
  when_to_use: string | null;
  category: string;
  vertical: string;
  icon: string | null;
  model: string;
  version: string;
  scope: "public" | "company";
  approval_state: string;
  install_count: number;
  pass_rate: string | null;
  is_installed: boolean;
}

export interface SchemaField {
  name: string;
  type: "string" | "number" | "boolean" | "object" | "array";
  required: boolean;
  description?: string;
}

export interface EvalCase {
  id: string;
  title: string;
  input: Record<string, unknown>;
  expected?: Record<string, unknown>;
  last_result?: "pass" | "fail" | "pending" | undefined;
}

export interface HandDraft {
  id: string;
  workspace_id: string;
  created_by: string;
  name: string | null;
  description: string | null;
  when_to_use: string | null;
  sample_use_cases: string[];
  category: string;
  icon: string | null;
  system_prompt: string;
  model: string;
  allowed_tools: string[];
  mcp_servers: Array<{ id: string; version?: string }>;
  xamun_skills: string[];
  max_turns: number;
  timeout_ms: number;
  permission_mode: string;
  input_schema: Record<string, unknown> | null;
  output_schema: Record<string, unknown> | null;
  structured_output: boolean;
  data_classification: string;
  pii_handling: string;
  cost_tier: string;
  approval_state: string;
  evals: EvalCase[];
  forked_from_hand_id: string | null;
  published_hand_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MyHand {
  install_id: string;
  hand_id: string;
  name: string;
  description: string;
  category: string;
  icon: string | null;
  model: string;
  scope: string;
  source_type: string;
  installed_version: string;
  latest_version: string;
  enabled: boolean;
  owner_name: string | null;
  cost_mtd: string;
  calls_30d: string;
  last_used: string | null;
  installed_at: string;
  approval_state: string;
}

export interface TestResult {
  output: string;
  telemetry: {
    latency_ms: number;
    tokens_in: number;
    tokens_out: number;
    cost_usd: number;
    model: string;
    credential_mode: string;
    payer: string;
  };
}

export interface EvalRunResult {
  results: Array<{ id: string; result: "pass" | "fail"; actual: string | null; fail_reason: string | null }>;
  pass_rate: number;
  pass_count: number;
  total: number;
}

// ─── Library hooks ─────────────────────────────────────────────────────────

export interface LibraryPage {
  hands: HandSummary[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface LibraryFilters {
  scope?: "public" | "company";
  category?: string;
  vertical?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface HandDetail extends HandSummary {
  system_prompt: string;
  allowed_tools: string[];
  mcp_servers: Array<{ id: string; version?: string }>;
  xamun_skills: string[];
  max_turns: number;
  timeout_ms: number;
  input_schema: Record<string, unknown> | null;
  output_schema: Record<string, unknown> | null;
  structured_output: boolean;
  data_classification: string;
  pii_handling: string;
  cost_tier: string;
  evals: unknown[];
}

export function useHandDetail(workspaceId: string | undefined, handId: string | undefined) {
  return useQuery<HandDetail>({
    queryKey: ["hands", "library", workspaceId, "detail", handId],
    queryFn: () =>
      apiGet<HandDetail>(`/api/workspaces/${workspaceId}/hands/library/${handId}`),
    enabled: !!workspaceId && !!handId,
  });
}

export function useHandLibrary(workspaceId: string | undefined, filters: LibraryFilters = {}) {
  const { scope = "public", category = "", vertical = "", search = "", page = 1, limit = 24 } = filters;
  const params = new URLSearchParams({ scope, page: String(page), limit: String(limit) });
  if (category && category !== "All") params.set("category", category);
  if (vertical && vertical !== "All") params.set("vertical", vertical);
  if (search) params.set("search", search);

  return useQuery<LibraryPage>({
    queryKey: ["hands", "library", workspaceId, scope, category, vertical, search, page, limit],
    queryFn: () =>
      apiGet<LibraryPage>(`/api/workspaces/${workspaceId}/hands/library?${params}`),
    enabled: !!workspaceId,
    placeholderData: (prev) => prev,
  });
}

export function useInstallHand(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (handId: string) =>
      apiPost(`/api/workspaces/${workspaceId}/hands/library/${handId}/install`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "library", workspaceId] });
      qc.invalidateQueries({ queryKey: ["hands", "my", workspaceId] });
    },
  });
}

export function useForkHand(workspaceId: string | undefined) {
  return useMutation({
    mutationFn: (handId: string) =>
      apiPost<{ draftId: string }>(
        `/api/workspaces/${workspaceId}/hands/library/${handId}/fork`,
        {},
      ),
  });
}

// ─── Builder hooks ─────────────────────────────────────────────────────────

export function useHandDrafts(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ["hands", "drafts", workspaceId],
    queryFn: () =>
      apiGet<{ drafts: HandDraft[] }>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts`,
      ).then((r) => r.drafts),
    enabled: !!workspaceId,
  });
}

export function useHandDraft(workspaceId: string | undefined, draftId: string | undefined) {
  return useQuery({
    queryKey: ["hands", "draft", workspaceId, draftId],
    queryFn: () =>
      apiGet<HandDraft>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}`,
      ),
    enabled: !!workspaceId && !!draftId,
  });
}

export function useCreateDraft(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<{ id: string }>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "drafts", workspaceId] });
    },
  });
}

export function useUpdateDraft(workspaceId: string | undefined, draftId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<HandDraft>) =>
      apiPatch(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}`,
        patch,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "draft", workspaceId, draftId] });
    },
  });
}

export function useTestDraft(workspaceId: string | undefined, draftId: string | undefined) {
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      apiPost<TestResult>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}/test`,
        { input },
      ),
  });
}

export function useRunEvals(workspaceId: string | undefined, draftId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<EvalRunResult>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}/evals/run`,
        {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "draft", workspaceId, draftId] });
    },
  });
}

export function useImprovePrompt(workspaceId: string | undefined, draftId: string | undefined) {
  return useMutation({
    mutationFn: () =>
      apiPost<{ improved_prompt: string }>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}/improve-prompt`,
        {},
      ),
  });
}

export function usePublishDraft(workspaceId: string | undefined, draftId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: string) =>
      apiPost<{ handId: string; version: string; pass_rate: number }>(
        `/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}/publish`,
        { version },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "drafts", workspaceId] });
      qc.invalidateQueries({ queryKey: ["hands", "library", workspaceId] });
      qc.invalidateQueries({ queryKey: ["hands", "my", workspaceId] });
    },
  });
}

export function useDeleteDraft(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      apiDelete(`/api/workspaces/${workspaceId}/hands/builder/drafts/${draftId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "drafts", workspaceId] });
    },
  });
}

// ─── My Hands hooks ────────────────────────────────────────────────────────

export function useMyHands(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ["hands", "my", workspaceId],
    queryFn: () =>
      apiGet<{ hands: MyHand[] }>(
        `/api/workspaces/${workspaceId}/hands/my`,
      ).then((r) => r.hands),
    enabled: !!workspaceId,
  });
}

export function useToggleHandInstall(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ installId, enabled }: { installId: string; enabled: boolean }) =>
      apiPatch(`/api/workspaces/${workspaceId}/hands/my/${installId}`, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "my", workspaceId] });
    },
  });
}

export function useUninstallHand(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (installId: string) =>
      apiDelete(`/api/workspaces/${workspaceId}/hands/my/${installId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hands", "my", workspaceId] });
      qc.invalidateQueries({ queryKey: ["hands", "library", workspaceId] });
    },
  });
}
